import { useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import { SVGLoader, type SVGResult } from 'three-stdlib';
import type { MeshData, SvgData, SvgDataProps } from '../types';
import { RENDER_ORDER_MARKER } from './RenderOrder';
import { useAbyssViewerContext } from './AbyssViewerContext';

const DEFAULT_Z_STEP = 0.0001;
const DEFAULT_RENDER_ORDER_STEP = 0;

const getCreateMaterial = (
  materialByColor: Map<number, THREE.MeshBasicMaterial>,
  styleColor: string,
  opacity: number,
  transparent: boolean,
  depthWrite: boolean,
  clippingPlanes: THREE.Plane[] | undefined
) => {
  const color = new THREE.Color().setStyle(styleColor);
  const materialKey = color.getHex();
  const existingMaterial = materialByColor.get(materialKey);
  if (existingMaterial) {
    return existingMaterial;
  }
  const clippingPlanesEntry = clippingPlanes ? { clippingPlanes } : {};
  const material = new THREE.MeshBasicMaterial({
    color,
    opacity,
    transparent,
    side: THREE.DoubleSide,
    depthWrite,
    ...clippingPlanesEntry,
  });
  materialByColor.set(materialKey, material);
  return material;
};

export const useSvgData = (
  svgProps?: SvgDataProps,
  enableClippingByVisibilityBox?: boolean
): SvgData | undefined => {
  const { url, renderOrder, renderOrderStep, zStep, transparent, depthWrite } = svgProps ?? {};
  const [svgResult, setSvgResult] = useState<SVGResult>();

  const { clippingPlanes: globalClippingPlanes } = useAbyssViewerContext();

  const clippingPlanes = (enableClippingByVisibilityBox ?? true) ? globalClippingPlanes : undefined;

  useEffect(() => {
    if (!url) {
      setSvgResult(undefined);
      return;
    }
    const loader = new SVGLoader();
    loader.load(
      !url.startsWith('<svg') ? url : `data:image/svg+xml;utf8,${url}`,
      (data: SVGResult) => {
        setSvgResult(data);
      }
    );
  }, [url]);

  const svgData = useMemo(() => {
    if (!svgResult) return undefined;
    const materialByColor = new Map<number, THREE.MeshBasicMaterial>();

    // need to flip y-coordinate of geometry (otherwise markers are flipped upsidedown)
    const matrixToFlipY = new THREE.Matrix4().makeScale(1, -1, 1);
    const zShiftMatrix4 = new THREE.Matrix4();

    const meshes: MeshData[] = [];

    let zShift = 0;
    let currentOrder = renderOrder ?? RENDER_ORDER_MARKER;

    svgResult.paths.forEach((path) => {
      const style = path.userData?.style;
      const fillColor = style.fill;

      if (fillColor !== undefined && fillColor !== 'none') {
        const material = getCreateMaterial(
          materialByColor,
          fillColor,
          style.fillOpacity,
          transparent ?? true,
          depthWrite ?? false,
          clippingPlanes
        );

        const shapes = SVGLoader.createShapes(path);

        for (const shape of shapes) {
          const geometry = new THREE.ShapeGeometry(shape);
          zShiftMatrix4.makeTranslation(0, 0, zShift).premultiply(matrixToFlipY);
          geometry.getAttribute('position').applyMatrix4(zShiftMatrix4);
          meshes.push({ geometry, material, renderOrder: currentOrder });
          zShift += zStep ?? DEFAULT_Z_STEP;
        }
      }
      currentOrder += renderOrderStep ?? DEFAULT_RENDER_ORDER_STEP;

      const strokeColor = style.stroke;

      if (strokeColor !== undefined && strokeColor !== 'none') {
        const material = getCreateMaterial(
          materialByColor,
          strokeColor,
          style.strokeOpacity,
          transparent ?? true,
          depthWrite ?? false,
          clippingPlanes
        );

        for (const subPath of path.subPaths) {
          const geometry = SVGLoader.pointsToStroke(subPath.getPoints(), style);

          if (geometry) {
            zShiftMatrix4.makeTranslation(0, 0, zShift).premultiply(matrixToFlipY);
            geometry.getAttribute('position').applyMatrix4(zShiftMatrix4);
            meshes.push({ geometry, material, renderOrder: currentOrder });
            zShift += zStep ?? DEFAULT_Z_STEP;
          }
        }
      }
      currentOrder += 1;
    });
    return { meshes, materials: [...materialByColor.values()] };
  }, [svgResult, renderOrder, renderOrderStep, zStep, depthWrite, transparent, clippingPlanes]);

  useEffect(() => {
    return () => {
      if (!svgData) return;
      svgData.materials.forEach((material) => material.dispose());
      svgData.meshes.forEach((mesh) => mesh.geometry.dispose());
    };
  }, [svgData]);

  return svgData;
};
